import { parseCalculatorMetricUnitMark } from "./calculator-metric-unit-mark.js";
import { provenanceRailMarkup } from "./provenance-indicator.js";

export const LOOT_ITEM_ROW_LAYOUT = Object.freeze({
    height: 58,
    metricWidth: 88,
    gap: 8,
    sankeyWidth: 380,
});

const ITEM_GRADE_TONES = new Set([
    "red",
    "yellow",
    "blue",
    "green",
    "white",
    "unknown",
]);
const METRIC_VALUE_STATES = new Set(["known", "unknown", "unavailable"]);
const CONTROL_FIELDS = new Set([
    "action",
    "ariaLabel",
    "breakdownId",
    "disabled",
    "expanded",
    "groupId",
    "itemId",
    "pressed",
    "traceId",
    "type",
]);

function trimString(value) {
    return String(value ?? "").trim();
}

function escapeHtml(value) {
    return String(value ?? "").replace(
        /[&<>"']/g,
        (char) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;",
        })[char] || char,
    );
}

function exactObject(value, fields, path) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object`);
    }
    const actual = Object.keys(value).sort();
    const expected = [...fields].sort();
    if (
        actual.length !== expected.length
        || actual.some((field, index) => field !== expected[index])
    ) {
        throw new TypeError(`${path} must contain exactly: ${expected.join(", ")}`);
    }
    return value;
}

function optionalText(value, path) {
    if (value === null) {
        return null;
    }
    if (typeof value !== "string") {
        throw new TypeError(`${path} must be a string or null`);
    }
    return value.trim();
}

function requiredText(value, path) {
    const text = optionalText(value, path);
    if (!text) {
        throw new TypeError(`${path} must be a non-empty string`);
    }
    return text;
}

function spriteIcon(name, className) {
    return `<svg class="fishy-icon ${escapeHtml(className)}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="/img/icons.svg#fishy-${escapeHtml(name)}"></use></svg>`;
}

export function itemGradeTone(grade, isPrize) {
    const normalized = trimString(grade).toLowerCase();
    if (isPrize === true || normalized === "prize" || normalized === "red") {
        return "red";
    }
    switch (normalized) {
        case "rare":
        case "yellow":
            return "yellow";
        case "highquality":
        case "high_quality":
        case "high-quality":
        case "blue":
            return "blue";
        case "general":
        case "green":
            return "green";
        case "trash":
        case "white":
            return "white";
        default:
            return "unknown";
    }
}

function parseControl(value, path) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object`);
    }
    for (const field of Object.keys(value)) {
        if (!CONTROL_FIELDS.has(field)) {
            throw new TypeError(`${path}.${field} is not supported`);
        }
    }
    if (value.type !== "static" && value.type !== "button") {
        throw new TypeError(`${path}.type must be static or button`);
    }
    const ariaLabel = requiredText(value.ariaLabel, `${path}.ariaLabel`);
    const action = value.action === undefined
        ? null
        : requiredText(value.action, `${path}.action`);
    if (value.type === "button" && !action) {
        throw new TypeError(`${path}.action is required for a button`);
    }
    const parseOptionalInteger = (field) => {
        if (value[field] === undefined || value[field] === null) {
            return null;
        }
        if (!Number.isInteger(value[field])) {
            throw new TypeError(`${path}.${field} must be an integer or null`);
        }
        return value[field];
    };
    const parseOptionalBoolean = (field) => {
        if (value[field] === undefined || value[field] === null) {
            return null;
        }
        if (typeof value[field] !== "boolean") {
            throw new TypeError(`${path}.${field} must be boolean or null`);
        }
        return value[field];
    };
    const parseOptionalString = (field) => {
        if (value[field] === undefined || value[field] === null) {
            return null;
        }
        return requiredText(value[field], `${path}.${field}`);
    };
    return Object.freeze({
        type: value.type,
        ariaLabel,
        action,
        breakdownId: parseOptionalString("breakdownId"),
        groupId: parseOptionalString("groupId"),
        itemId: parseOptionalInteger("itemId"),
        traceId: parseOptionalString("traceId"),
        pressed: parseOptionalBoolean("pressed"),
        expanded: parseOptionalBoolean("expanded"),
        disabled: parseOptionalBoolean("disabled") ?? false,
    });
}

function controlAttributes(control, controlName) {
    const attributes = [
        `data-fishy-loot-row-control="${escapeHtml(controlName)}"`,
        `aria-label="${escapeHtml(control.ariaLabel)}"`,
    ];
    if (control.action) {
        attributes.push(`data-action="${escapeHtml(control.action)}"`);
    }
    if (control.breakdownId) {
        attributes.push(
            `data-calculator-breakdown-id="${escapeHtml(control.breakdownId)}"`,
        );
    }
    if (control.groupId) {
        attributes.push(`data-group-id="${escapeHtml(control.groupId)}"`);
    }
    if (control.itemId !== null) {
        attributes.push(`data-item-id="${control.itemId}"`);
    }
    if (control.traceId) {
        attributes.push(`data-trace-id="${escapeHtml(control.traceId)}"`);
    }
    if (control.pressed !== null) {
        attributes.push(`aria-pressed="${control.pressed}"`);
    }
    if (control.expanded !== null) {
        attributes.push(`aria-expanded="${control.expanded}"`);
    }
    if (control.disabled) {
        attributes.push("disabled");
    }
    if (control.type === "static") {
        attributes.push('role="group"');
    }
    if (control.type === "static" && control.breakdownId) {
        attributes.push('tabindex="0"');
    }
    return attributes.join(" ");
}

function controlMarkup(control, controlName, className, contents) {
    const attributes = controlAttributes(control, controlName);
    if (control.type === "button") {
        return `<button class="${className}" type="button" ${attributes}>${contents}</button>`;
    }
    return `<div class="${className}" ${attributes}>${contents}</div>`;
}

function parseMetricValue(value, path) {
    const fields = Object.prototype.hasOwnProperty.call(value ?? {}, "state")
        ? ["state", "text", "unitMark"]
        : ["text", "unitMark"];
    exactObject(value, fields, path);
    const state = value.state ?? "known";
    if (!METRIC_VALUE_STATES.has(state)) {
        throw new TypeError(`${path}.state must be known, unknown, or unavailable`);
    }
    return Object.freeze({
        state,
        text: requiredText(value.text, `${path}.text`),
        unitMark: parseCalculatorMetricUnitMark(value.unitMark, `${path}.unitMark`),
    });
}

function parseTone(value, path) {
    exactObject(value, ["fillColor", "strokeColor", "textColor"], path);
    return Object.freeze({
        fillColor: requiredText(value.fillColor, `${path}.fillColor`),
        strokeColor: requiredText(value.strokeColor, `${path}.strokeColor`),
        textColor: requiredText(value.textColor, `${path}.textColor`),
    });
}

function metricUnitName(unitMark) {
    if (unitMark.kind === "text") {
        return "percent";
    }
    if (unitMark.kind === "icon") {
        return unitMark.iconId;
    }
    return "none";
}

function metricRateBasis(unitMark) {
    if (unitMark.kind === "text") {
        return "percent";
    }
    if (unitMark.kind === "icon" && unitMark.iconId === "weight") {
        return "rawWeight";
    }
    return "";
}

function metricWatermarkMarkup(unitMark) {
    if (unitMark.kind === "none") {
        return "";
    }
    if (unitMark.kind === "text") {
        return `<span class="fishy-loot-row__metric-mark fishy-loot-row__metric-mark--text" aria-hidden="true" data-loot-rate-watermark="percent">${escapeHtml(unitMark.text)}</span>`;
    }
    const watermark = unitMark.iconId === "weight" ? "rawWeight" : unitMark.iconId;
    return `<span class="fishy-loot-row__metric-mark" aria-hidden="true" data-loot-rate-watermark="${escapeHtml(watermark)}">${spriteIcon(unitMark.iconId, "fishy-loot-row__metric-mark-icon")}</span>`;
}

function metricValueMarkup(value, position) {
    const valueMarkup = value.state !== "known"
        ? `${spriteIcon("question-mark", "size-4")}<span class="sr-only">${escapeHtml(value.text)}</span>`
        : escapeHtml(value.text);
    return `<span class="fishy-loot-row__metric-value fishy-loot-row__metric-value--${position}" data-fishy-loot-metric-unit="${escapeHtml(metricUnitName(value.unitMark))}">${metricWatermarkMarkup(value.unitMark)}<span class="fishy-loot-row__metric-text" data-rate-basis="${escapeHtml(metricRateBasis(value.unitMark))}">${valueMarkup}</span></span>`;
}

export function renderLootMetricCell(metric, slot = "standalone") {
    exactObject(metric, ["control", "primary", "secondary", "tone"], "loot metric");
    if (!["standalone", "leading", "trailing"].includes(slot)) {
        throw new TypeError("loot metric slot must be standalone, leading, or trailing");
    }
    const primary = parseMetricValue(metric.primary, "loot metric.primary");
    const secondary = metric.secondary === null
        ? null
        : parseMetricValue(metric.secondary, "loot metric.secondary");
    const tone = parseTone(metric.tone, "loot metric.tone");
    const control = parseControl(metric.control, "loot metric.control");
    const hook = slot === "leading"
        ? " data-fishy-loot-row-leading-metric"
        : slot === "trailing"
            ? " data-fishy-loot-row-trailing-metric"
            : " data-fishy-loot-row-metric";
    const style = [
        `--fishy-loot-row-metric-width:${LOOT_ITEM_ROW_LAYOUT.metricWidth}px`,
        `--fishy-loot-row-fill:${tone.fillColor}`,
        `--fishy-loot-row-stroke:${tone.strokeColor}`,
        `--fishy-loot-row-text:${tone.textColor}`,
    ].map(escapeHtml).join(";");
    const contents = metricValueMarkup(primary, "primary")
        + (secondary
            ? `<span class="fishy-loot-row__metric-divider" aria-hidden="true"></span>${metricValueMarkup(secondary, "secondary")}`
            : "");
    const markup = controlMarkup(
        control,
        slot === "standalone" ? "metric" : `${slot}Metric`,
        `fishy-loot-row__metric fishy-loot-row__metric--${slot}`,
        contents,
    );
    return markup.replace(
        /^<([a-z]+) class=/,
        `<$1${hook} style="${style}" class=`,
    );
}

function parseItem(value) {
    exactObject(
        value,
        [
            "control",
            "iconGradeTone",
            "iconUrl",
            "label",
            "priceLabel",
            "priceText",
            "quantityLabel",
            "quantityText",
        ],
        "loot item",
    );
    const label = requiredText(value.label, "loot item.label");
    const iconUrl = optionalText(value.iconUrl, "loot item.iconUrl");
    const iconGradeTone = requiredText(
        value.iconGradeTone,
        "loot item.iconGradeTone",
    );
    if (!ITEM_GRADE_TONES.has(iconGradeTone)) {
        throw new TypeError("loot item.iconGradeTone is not canonical");
    }
    const priceText = optionalText(value.priceText, "loot item.priceText");
    const priceLabel = requiredText(value.priceLabel, "loot item.priceLabel");
    const quantityText = optionalText(value.quantityText, "loot item.quantityText");
    const quantityLabel = optionalText(value.quantityLabel, "loot item.quantityLabel");
    if ((quantityText === null) !== (quantityLabel === null)) {
        throw new TypeError(
            "loot item.quantityText and loot item.quantityLabel must both be null or present",
        );
    }
    return Object.freeze({
        label,
        iconUrl,
        iconGradeTone,
        priceText,
        priceLabel,
        quantityText,
        quantityLabel,
        control: parseControl(value.control, "loot item.control"),
    });
}

function itemPriceMarkup(item) {
    if (!item.priceText) {
        return "";
    }
    return `<span class="fishy-loot-row__price"><span class="sr-only">${escapeHtml(`${item.priceLabel}: `)}</span>${spriteIcon("coin-2-fill", "size-3.5")}<span class="fishy-loot-row__price-text">${escapeHtml(item.priceText)}</span></span>`;
}

function itemQuantityMarkup(item) {
    if (!item.quantityText) {
        return "";
    }
    return `<span class="fishy-loot-row__quantity"><span class="sr-only">${escapeHtml(item.quantityLabel)}</span><span aria-hidden="true">${escapeHtml(item.quantityText)}</span></span>`;
}

function itemToneClass(item) {
    return `fishy-item-grade-${escapeHtml(item.iconGradeTone)}`;
}

function itemIdentityMarkup(item) {
    const toneClass = itemToneClass(item);
    const iconMarkup = item.iconUrl
        ? `<span class="fishy-item-icon-frame is-sm ${toneClass}"><img class="fishy-item-icon" src="${escapeHtml(item.iconUrl)}" alt="" loading="lazy" decoding="async"></span>`
        : `<span class="fishy-item-icon-frame is-sm ${toneClass}" aria-hidden="true"><span class="fishy-item-icon-fallback ${toneClass}">${escapeHtml(item.label.charAt(0).toUpperCase() || "?")}</span></span>`;
    const metadata = itemPriceMarkup(item) + itemQuantityMarkup(item);
    return `<span class="fishy-item-row fishy-loot-row__item-identity ${toneClass}">${iconMarkup}<span class="fishy-loot-row__item-copy"><span class="fishy-domain-label fishy-item-label fishy-loot-row__item-label truncate">${escapeHtml(item.label)}</span>${metadata ? `<span class="fishy-loot-row__item-metadata">${metadata}</span>` : ""}</span></span>`;
}

function itemMarkup(
    item,
    provenanceSegments,
    provenanceLabel,
    provenanceDetailBindings,
) {
    const identity = controlMarkup(
        item.control,
        "item",
        "fishy-loot-row__item-control",
        itemIdentityMarkup(item),
    );
    const provenance = provenanceSegments.length
        ? provenanceRailMarkup(provenanceSegments, {
            label: provenanceLabel,
            detailBindings: provenanceDetailBindings,
        })
        : "";
    return `<div class="fishy-item-row fishy-item-row--surface fishy-loot-row__item ${itemToneClass(item)}" data-fishy-loot-row-item-surface>${identity}${provenance}</div>`;
}

export function lootItemRowMarkup(
    presentation,
    { provenanceDetailBindings = null } = {},
) {
    exactObject(
        presentation,
        [
            "item",
            "leadingMetric",
            "provenanceLabel",
            "provenanceSegments",
            "rowId",
            "trailingMetric",
        ],
        "loot item row",
    );
    const rowId = presentation.rowId === null
        ? null
        : requiredText(presentation.rowId, "loot item row.rowId");
    const item = parseItem(presentation.item);
    if (!Array.isArray(presentation.provenanceSegments)) {
        throw new TypeError("loot item row.provenanceSegments must be an array");
    }
    const provenanceLabel = requiredText(
        presentation.provenanceLabel,
        "loot item row.provenanceLabel",
    );
    const leadingMetric = presentation.leadingMetric === null
        ? ""
        : renderLootMetricCell(presentation.leadingMetric, "leading");
    const trailingMetric = presentation.trailingMetric === null
        ? ""
        : renderLootMetricCell(presentation.trailingMetric, "trailing");
    const classes = [
        "fishy-loot-row",
        presentation.leadingMetric === null
            ? "fishy-loot-row--without-leading"
            : "fishy-loot-row--with-leading",
        presentation.trailingMetric === null
            ? "fishy-loot-row--without-trailing"
            : "fishy-loot-row--with-trailing",
    ].join(" ");
    const rowAttribute = rowId
        ? ` data-calculator-row-id="${escapeHtml(rowId)}"`
        : "";
    const layoutStyle = [
        `--fishy-loot-row-height:${LOOT_ITEM_ROW_LAYOUT.height}px`,
        `--fishy-loot-row-metric-width:${LOOT_ITEM_ROW_LAYOUT.metricWidth}px`,
        `--fishy-loot-row-gap:${LOOT_ITEM_ROW_LAYOUT.gap}px`,
    ].join(";");
    return `<div class="${classes}" style="${layoutStyle}" data-fishy-loot-row${rowAttribute}>${leadingMetric}${itemMarkup(item, presentation.provenanceSegments, provenanceLabel, provenanceDetailBindings)}${trailingMetric}</div>`;
}
